import { useRef, useState } from 'react';
import { getPalette, useCanvas } from '../../lib/canvas';
import type { LiveRefs } from '../../lib/useStreamLive';
import { Panel, Segmented } from '../Panel';

type Gain = 'auto' | 0 | 6 | 12 | 18 | 24;

/**
 * L/R Lissajous display in M/S orientation (rotated 45 degrees: mono is vertical,
 * a left-only signal leans to the upper left, out-of-phase content lies flat).
 * Points are drawn into a persistence buffer that fades each frame (phosphor).
 */
export function Goniometer({ live, pairLabel, pairControl }: { live: React.RefObject<LiveRefs>; pairLabel: string; pairControl?: React.ReactNode }) {
  const [gain, setGain] = useState<Gain>('auto');
  const persist = useRef<HTMLCanvasElement | null>(null);
  const readPos = useRef(0);
  const autoGain = useRef(1);
  const gainRef = useRef(gain);
  gainRef.current = gain;

  const ref = useCanvas((ctx, w, h) => {
    const p = getPalette();
    const size = Math.min(w, h);
    const dpr = window.devicePixelRatio || 1;
    let buf = persist.current;
    if (!buf || buf.width !== Math.round(size * dpr)) {
      buf = document.createElement('canvas');
      buf.width = Math.round(size * dpr);
      buf.height = Math.round(size * dpr);
      persist.current = buf;
    }
    const b = buf.getContext('2d')!;
    const S = buf.width;
    const c = S / 2;

    // Fade previous points towards the background.
    b.globalCompositeOperation = 'source-over';
    b.fillStyle = p.scopeBg;
    b.globalAlpha = 0.1;
    b.fillRect(0, 0, S, S);
    b.globalAlpha = 1;

    const ring = live.current?.pairs;
    if (ring) {
      const total = ring.written;
      let from = readPos.current;
      if (total - from > ring.capacity) from = total - ring.capacity;
      if (from > total) from = total;
      // Track the recent peak of M and S for auto gain.
      let peak = 0;
      const g = gainRef.current;
      const manual = g === 'auto' ? null : Math.pow(10, g / 20);
      const scale = (manual ?? autoGain.current) * c * 0.92;
      b.fillStyle = `rgb(${p.scopeInk} / 0.6)`;
      b.strokeStyle = `rgb(${p.scopeInk} / 0.22)`;
      b.lineWidth = dpr;
      const dot = Math.max(1.5, dpr * 1.4);
      // A faint trace joins consecutive points (lineup tone draws a line), dots mark the samples.
      b.beginPath();
      for (let i = Math.max(from - 1, total - ring.capacity, 0); i < total; i++) {
        const k = (i % ring.capacity) * 2;
        const l = ring.data[k];
        const r = ring.data[k + 1];
        const px = c + (r - l) * Math.SQRT1_2 * scale;
        const py = c - (l + r) * Math.SQRT1_2 * scale;
        if (i === Math.max(from - 1, total - ring.capacity, 0)) b.moveTo(px, py);
        else b.lineTo(px, py);
      }
      b.stroke();
      for (let i = from; i < total; i++) {
        const k = (i % ring.capacity) * 2;
        const l = ring.data[k];
        const r = ring.data[k + 1];
        const x = (r - l) * Math.SQRT1_2; // side: positive = right
        const y = (l + r) * Math.SQRT1_2; // mid: positive = up
        const a = Math.max(Math.abs(x), Math.abs(y));
        if (a > peak) peak = a;
        b.fillRect(c + x * scale - dot / 2, c - y * scale - dot / 2, dot, dot);
      }
      readPos.current = total;
      if (peak > 0) {
        // Fast attack, slow release; never more than +40 dB of gain.
        const want = Math.min(100, 1 / peak);
        autoGain.current = want < autoGain.current ? want : autoGain.current + (want - autoGain.current) * 0.02;
      }
    }

    // Compose: background, persistence buffer, graticule on top.
    ctx.clearRect(0, 0, w, h);
    const ox = (w - size) / 2;
    const oy = (h - size) / 2;
    ctx.drawImage(buf, ox, oy, size, size);
    const cx = ox + size / 2;
    const cy = oy + size / 2;
    const rad = (size / 2) * 0.92;
    ctx.strokeStyle = p.scopeGrid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, rad, 0, Math.PI * 2);
    ctx.moveTo(cx, cy - rad);
    ctx.lineTo(cx, cy + rad);
    ctx.moveTo(cx - rad, cy);
    ctx.lineTo(cx + rad, cy);
    const d = rad * Math.SQRT1_2;
    ctx.moveTo(cx - d, cy - d);
    ctx.lineTo(cx + d, cy + d);
    ctx.moveTo(cx + d, cy - d);
    ctx.lineTo(cx - d, cy + d);
    ctx.stroke();
    ctx.fillStyle = p.scale;
    ctx.font = `11px ${p.font}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('M', cx, cy - rad + 10);
    ctx.fillText('L', cx - d + 8, cy - d + 2);
    ctx.fillText('R', cx + d - 8, cy - d + 2);
    ctx.fillText('+S', cx - rad + 12, cy - 8);
    ctx.fillText('-S', cx + rad - 12, cy - 8);
  });

  return (
    <Panel
      title={`Goniometer ${pairLabel}`}
      actions={
        <>
        {pairControl}
        <Segmented<Gain>
          label="Goniometer gain"
          value={gain}
          onChange={setGain}
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 0, label: '0' },
            { value: 12, label: '+12' },
            { value: 24, label: '+24' },
          ]}
        />
        </>
      }
    >
      <canvas ref={ref} className="gonio-canvas inset" role="img" aria-label="Goniometer showing the stereo image" />
      <p className="muted small">Vertical line: mono. Horizontal line: out of phase. Wide cloud: wide or uncorrelated stereo.</p>
    </Panel>
  );
}
